import { supabase } from '../lib/supabase';
import { normalizePhone } from '../../lib/utils/phone.js';

const ENGAGEMENT_LEVELS = new Set(['High', 'Medium', 'Low']);
const IMPORT_CHUNK_SIZE = 500;

/** Parse date strings to YYYY-MM-DD for Postgres DATE columns */
export function parseDateValue(value) {
  if (!value?.trim()) return null;
  const v = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    return d.toISOString().split('T')[0];
  }
  return null;
}

/** NurtureFlow is past-clients-only — every contact is Past Client. */
export function sanitizeLifecycleStage(_value, _fallback = 'Past Client') {
  return 'Past Client';
}

export function sanitizeEngagement(value, fallback = 'Medium') {
  if (!value) return fallback;
  const trimmed = String(value).trim();
  if (ENGAGEMENT_LEVELS.has(trimmed)) return trimmed;
  const cap = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  return ENGAGEMENT_LEVELS.has(cap) ? cap : fallback;
}

function chunkArray(arr, size) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/**
 * Import approved clients with chunked bulk upsert, phone normalization, and per-agent dedup.
 * @param {Array<object>} clients
 * @param {{ defaultLifecycleStage?: string, updateExisting?: boolean }} options
 */
export async function importPastClients(clients, options = {}) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    const defaultStage = options.defaultLifecycleStage || 'Past Client';
    const updateExisting = options.updateExisting !== false;

    const prepared = [];
    const errors = [];

    for (let i = 0; i < (clients || []).length; i++) {
      const c = clients[i] || {};
      const name = String(c.name || '').trim();
      if (!name) {
        errors.push(`Row ${i + 1}: Missing name`);
        continue;
      }

      const phoneRaw = c.phone ? String(c.phone).trim() : '';
      const phone = normalizePhone(phoneRaw) || (phoneRaw || null);
      const email = c.email?.trim() ? String(c.email).trim().toLowerCase() : null;
      if (!phone && !email) {
        errors.push(`Row ${i + 1} (${name}): Missing phone and email`);
        continue;
      }

      let tags = [];
      if (Array.isArray(c.tags)) {
        tags = c.tags.map((t) => String(t).trim()).filter(Boolean);
      } else if (typeof c.tags === 'string' && c.tags.trim()) {
        tags = c.tags.split(',').map((t) => t.trim()).filter(Boolean);
      }

      prepared.push({
        name,
        email,
        phone,
        location: c.location?.trim() || null,
        notes: c.notes?.trim() || null,
        lifecycle_stage: sanitizeLifecycleStage(c.lifecycleStage || c.lifecycle_stage, defaultStage),
        engagement_level: sanitizeEngagement(c.engagementLevel || c.engagement_level, 'Medium'),
        birthday: parseDateValue(c.birthday),
        home_closing_date: parseDateValue(c.homeClosingDate || c.home_closing_date),
        agent_id: user.id,
        tags,
      });
    }

    if (prepared.length === 0) {
      return {
        data: null,
        error: { message: errors[0] || 'No valid clients to import.' },
      };
    }

    const { data: existingRows, error: existingError } = await supabase
      .from('clients')
      .select('id, phone, email')
      .eq('agent_id', user.id);

    if (existingError) {
      return { data: null, error: { message: existingError.message } };
    }

    const byPhone = new Map(); // normalizedPhone -> client id (string UUID)
    const byEmail = new Map(); // email -> client id
    // Within-file pending inserts: map key -> index into toInsert so duplicates merge
    const pendingByPhone = new Map();
    const pendingByEmail = new Map();

    for (const row of existingRows || []) {
      const np = normalizePhone(row.phone);
      if (np) byPhone.set(np, row.id);
      if (row.email) byEmail.set(String(row.email).toLowerCase(), row.id);
    }

    const toInsert = [];
    const toUpdate = [];
    const tagJobs = [];

    for (const client of prepared) {
      const np = normalizePhone(client.phone);
      let existingId = null;

      if (updateExisting) {
        if (np && byPhone.has(np)) {
          existingId = byPhone.get(np);
        } else if (client.email && byEmail.has(client.email)) {
          existingId = byEmail.get(client.email);
        }
      }

      const { tags, ...clientInfo } = client;

      if (existingId) {
        toUpdate.push({ id: existingId, ...clientInfo, updated_at: new Date().toISOString() });
        if (tags.length) tagJobs.push({ clientId: existingId, tags });
        continue;
      }

      // Within-file duplicate: merge onto the first pending insert for this phone/email
      let pendingIdx = null;
      if (np && pendingByPhone.has(np)) {
        pendingIdx = pendingByPhone.get(np);
      } else if (client.email && pendingByEmail.has(client.email)) {
        pendingIdx = pendingByEmail.get(client.email);
      }

      if (pendingIdx != null && toInsert[pendingIdx]) {
        const prev = toInsert[pendingIdx];
        // Prefer non-empty fields from the later row when previous is empty
        toInsert[pendingIdx] = {
          ...prev,
          name: clientInfo.name || prev.name,
          email: clientInfo.email || prev.email,
          phone: clientInfo.phone || prev.phone,
          location: clientInfo.location || prev.location,
          notes: clientInfo.notes || prev.notes,
          birthday: clientInfo.birthday || prev.birthday,
          home_closing_date: clientInfo.home_closing_date || prev.home_closing_date,
          lifecycle_stage: clientInfo.lifecycle_stage || prev.lifecycle_stage,
          engagement_level: clientInfo.engagement_level || prev.engagement_level,
          __tags: Array.from(new Set([...(prev.__tags || []), ...tags])),
        };
        continue;
      }

      const insertIdx = toInsert.length;
      toInsert.push({ ...clientInfo, __tags: tags });
      if (np) pendingByPhone.set(np, insertIdx);
      if (client.email) pendingByEmail.set(client.email, insertIdx);
    }

    const results = {
      successful: 0,
      updated: 0,
      failed: 0,
      // Contacts imported without a phone — can't receive SMS nurture until one is added.
      missingPhone: prepared.filter((c) => !c.phone).length,
      errors: [...errors],
    };

    for (const chunk of chunkArray(toUpdate, IMPORT_CHUNK_SIZE)) {
      for (const row of chunk) {
        const { id, agent_id: _agentId, ...updates } = row;
        const { error: updateError } = await supabase
          .from('clients')
          .update(updates)
          .eq('id', id)
          .eq('agent_id', user.id);

        if (updateError) {
          results.failed++;
          results.errors.push(`Failed to update ${row.email || row.phone || row.name}: ${updateError.message}`);
        } else {
          results.updated++;
        }
      }
    }

    for (const chunk of chunkArray(toInsert, IMPORT_CHUNK_SIZE)) {
      const payload = chunk.map(({ __tags, ...rest }) => rest);
      const { data: inserted, error: insertError } = await supabase
        .from('clients')
        .insert(payload)
        .select('id, phone, email, name');

      if (insertError) {
        for (const item of chunk) {
          const { __tags, ...rest } = item;
          const { data: one, error: oneErr } = await supabase
            .from('clients')
            .insert(rest)
            .select('id')
            .single();
          if (oneErr) {
            results.failed++;
            results.errors.push(`Failed to import ${rest.email || rest.phone || rest.name}: ${oneErr.message}`);
          } else {
            results.successful++;
            if (__tags?.length) tagJobs.push({ clientId: one.id, tags: __tags });
          }
        }
        continue;
      }

      results.successful += (inserted || []).length;
      for (let i = 0; i < (inserted || []).length; i++) {
        const row = inserted[i];
        const tags = chunk[i]?.__tags || [];
        if (tags.length) tagJobs.push({ clientId: row.id, tags });
      }
    }

    for (const job of tagJobs) {
      if (!job.clientId || !job.tags?.length) continue;
      await supabase.from('client_tags').delete().eq('client_id', job.clientId);
      const tagInserts = job.tags.map((tag) => ({
        client_id: job.clientId,
        tag_name: tag,
      }));
      for (const tagChunk of chunkArray(tagInserts, IMPORT_CHUNK_SIZE)) {
        const { error: tagError } = await supabase.from('client_tags').insert(tagChunk);
        if (tagError) {
          results.errors.push(`Tag insert failed: ${tagError.message}`);
        }
      }
    }

    return { data: results, error: null };
  } catch (error) {
    return { data: null, error: { message: error.message || 'Import failed. Please try again.' } };
  }
}
